import React, { useCallback, type MouseEvent } from 'react';
import ChevronUp from '@atlaskit/icon/core/chevron-up';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';

import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	usePreviousIssueKey,
	useSelectedIssueActions,
} from '../../../../../../controllers/selected-issue/hooks.tsx';
import messages from './messages.tsx';

const IssuePaginationPreviousButton = () => {
	const { formatMessage } = useIntl();

	const actions = useSelectedIssueActions();
	const selectPreviousIssue = actions.selectPreviousIssue;

	const issueKey = usePreviousIssueKey();
	const isDisabled = !issueKey;
	const previousIssueMessage = issueKey
		? formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.previousIssueTooltipIssueTermRefresh
					: messages.previousIssueTooltip,
				{ issueKey },
			)
		: formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.previousIssueWithoutKeyTooltipIssueTermRefresh
					: messages.previousIssueWithoutKeyTooltip,
			);

	const onClick = useCallback(
		(_: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			if (selectPreviousIssue()) {
				fireUIAnalytics(analyticsEvent, 'previousIssue');
			}
		},
		[selectPreviousIssue],
	);

	return (
		<IconButton
			icon={(iconProps) => <ChevronUp {...iconProps} size="small" />}
			label={
				isDisabled
					? formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.disabledButtonLabelIssueTermRefresh
								: messages.disabledButtonLabel,
						)
					: previousIssueMessage
			}
			onClick={onClick}
			appearance="subtle"
			testId="issue-navigator.common.ui.issue-app.header-actions.issue-pagination.previous-button"
			isDisabled={isDisabled}
			spacing="compact"
			isTooltipDisabled={isDisabled}
		/>
	);
};

export default IssuePaginationPreviousButton;
